"use client";

import { api } from "~/trpc/react";
import { useInboxSocket } from "./useInboxSocket";

/**
 * The badge's own read options — deliberately NOT `MESSAGING_READ_OPTIONS`
 * (the inbox list's own, in `~/app/(site)/messages/_hooks/useMessagesInbox`).
 *
 * This query is mounted by `NavBarActionsClient` on every route, so `staleTime: 0` +
 * `refetchOnMount: "always"` there means every route change and every tab
 * focus fetches up to 50 conversation summaries just to render one integer.
 * A real stale time trades a little badge staleness for not hammering the
 * conversations endpoint on every navigation; a page that's actually showing
 * the inbox still gets `MESSAGING_READ_OPTIONS`'s freshness on its own query.
 */
const MESSAGING_BADGE_READ_OPTIONS = {
  staleTime: 30 * 1000,
  refetchOnWindowFocus: true,
} as const;

/**
 * Total unread across the inbox, for the nav badge.
 *
 * DELIBERATELY APPROXIMATE. There is no `/messaging/unread-count` route, so this
 * sums the first page at the API's maximum limit — 50 conversations. It
 * undercounts only when more than 50 threads have been more recently active than
 * an unread one, which is not a state a person with unread messages is usually in.
 *
 * Its own key, not the inbox's: `{ limit: 50 }` on a plain `useQuery` is a
 * different input AND a different query type than the inbox's
 * `useInfiniteQuery({ filter: "all", limit: 20 })`, so invalidating
 * `listConversations` sweeps both, but neither request satisfies the other's
 * cache entry. `MESSAGING_BADGE_READ_OPTIONS` is what keeps this one cheap.
 *
 * Also holds its own `useInboxSocket` subscription over those same 50
 * conversations, mounted from `NavBarActionsClient` — i.e. on every authenticated
 * route, not just `/messages`. Without it the badge only moved on the
 * 30s-stale refetch or a tab refocus, so a message that arrived while the
 * reader stayed put on, say, the dashboard needed a manual refresh to show
 * up at all. A `message.created` for any of these 50 now invalidates
 * `listConversations` directly, the same as it does for the open inbox list.
 */
export function useMessagingUnreadCount(enabled: boolean) {
  const query = api.messaging.listConversations.useQuery(
    { limit: 50 },
    {
      enabled,
      // Reduced to a primitive total + a sorted, joined id string (not the
      // raw array) so structural sharing hands back the SAME object across
      // refetches whenever neither actually changed — otherwise every 30s
      // refetch/focus/socket-driven invalidate produces a new array and
      // re-renders the whole nav for an unchanged badge. Sorted because
      // `useInboxSocket` immediately reduces its `conversationIds` param to
      // a sorted, joined key anyway; joining here means a reorder-only
      // response (e.g. last_message_at bump) doesn't count as a change.
      select: (response) => ({
        total: response.data.reduce((sum, row) => sum + row.unread_count, 0),
        ids: response.data
          .map((row) => row.id)
          .sort()
          .join(","),
      }),
      ...MESSAGING_BADGE_READ_OPTIONS,
    },
  );

  const conversationIdsKey = query.data?.ids ?? "";
  useInboxSocket(conversationIdsKey, enabled);

  return query.data?.total ?? 0;
}

/**
 * Unread on one booking's threads, for the chat button on a booking page.
 *
 * SUMMED across matching threads rather than first-match: nothing promises one
 * conversation per booking, and a support thread about the same booking is a
 * second row.
 */
export function useBookingUnreadCount(bookingId: string | undefined) {
  const query = api.messaging.listConversations.useQuery(
    { limit: 50 },
    {
      enabled: !!bookingId,
      select: (response) =>
        response.data.reduce(
          (total, row) =>
            row.booking_id === bookingId ? total + row.unread_count : total,
          0,
        ),
      ...MESSAGING_BADGE_READ_OPTIONS,
    },
  );

  return query.data ?? 0;
}
